import { create } from "zustand" // move to lib/states
import { createJSONStorage, persist } from "zustand/middleware"

import type { PersistedComposerEntry } from "./composer-state-storage"
import { pruneEntries, writeWithQuotaRecovery } from "./composer-state-storage"

export type ComposerSource = "user" | "ai" | null

interface SingleComposerState {
	content: string

	commentContent: string

	// Origin of `content`. Drives the AI-draft badge state and the
	// auto-seed gate so we don't silently overwrite user-typed drafts.
	source: ComposerSource

	// `aiDraftGeneratedAt` of the AI draft we most recently applied to this
	// thread. Stays set even after the user has typed (source flips to "user")
	// so a *newer* AI draft generation surfaces as opt-in instead of being
	// auto-applied on top of an accepted draft.
	appliedAiDraftAt: number | null

	// Drives which entries survive pruning when the store is trimmed.
	updatedAt: number
}

interface ComposersState {
	entries: Record<string, SingleComposerState>

	setEntry: (id: string, entry: Partial<SingleComposerState>) => void
}

const STORAGE_KEY = "composers-state"

const quotaSafeStorage = createJSONStorage(() => ({
	getItem: (key: string) => localStorage.getItem(key),
	setItem: (key: string, value: string) =>
		writeWithQuotaRecovery({
			key,
			value,
			setItem: (k, v) => localStorage.setItem(k, v),
			removeItem: (k) => localStorage.removeItem(k),
		}),
	removeItem: (key: string) => localStorage.removeItem(key),
}))

export const useComposerState = create<ComposersState>()(
	persist(
		(set, get) => ({
			entries: {},
			setEntry: (id, entry) => {
				const prevEntries = get().entries
				const prevEntry = prevEntries[id]
				set({
					entries: {
						...prevEntries,
						[id]: {
							content:
								typeof entry.content === "string"
									? entry.content
									: prevEntry?.content
										? prevEntry.content
										: "",
							commentContent:
								typeof entry.commentContent === "string"
									? entry.commentContent
									: prevEntry?.commentContent
										? prevEntry.commentContent
										: "",
							source:
								"source" in entry
									? (entry.source ?? null)
									: (prevEntry?.source ?? null),
							appliedAiDraftAt:
								"appliedAiDraftAt" in entry
									? (entry.appliedAiDraftAt ?? null)
									: (prevEntry?.appliedAiDraftAt ?? null),
							updatedAt: Date.now(),
						},
					},
				})
			},
		}),
		{
			name: STORAGE_KEY,
			storage: quotaSafeStorage,
			partialize: (state) => ({
				entries: pruneEntries(
					state.entries as Record<string, PersistedComposerEntry>,
				),
			}),
		},
	),
)
